import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { match, P } from 'ts-pattern';
import { useActiveRealmAPIContext } from '@interxfi/api-client';
import { useIxfHoursV1ExceptionQueryServiceExceptionGet } from '@interxfi/api-client/generated/hooks';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { DEFAULT_WRAPPING_LAYOUT_COLUMN, WrappingLayoutContainer } from '@interxfi/design-system/components/layout-container';
import { LAYOUT_ZERO_CONTAINER_PADDING } from '@interxfi/design-system/components/layout-container/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import ArchiveIcon from '@interxfi/design-system/icons/react/icon-archive';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import { type IxfHoursV1ExceptionState, IxfHoursV1ExceptionStatus } from '@interxfi/types';
import { ArchiveHoursExceptionDialog } from '../../../../../../../features/hours/exception/archive-hours-exception-dialog';
import { HoursExceptionEventDrawer } from '../../../../../../../features/hours/exception/hours-exception-event';
import { getHoursExceptionStateListItems } from '../../../../../../../features/hours/exception/hours-exception-list-items';
import { getHoursExceptionDetailPath } from '../../../../../../../features/hours/exception/hours-exception-routing';
import { HoursExceptionStatusPill } from '../../../../../../../features/hours/exception/hours-exception-status-pill';
import { HoursExceptionUpdateDrawer } from '../../../../../../../features/hours/exception/hours-exception-update-drawer';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/hours/_hours/exception/_exception/$exceptionId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `hours-exception-${params.exceptionId}`,
        label: 'Exception',
        to: getHoursExceptionDetailPath(params.exceptionId),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Exception'),
  component: HoursExceptionDetail,
});

function getListItems(data: IxfHoursV1ExceptionState | undefined) {
  const listItems = getHoursExceptionStateListItems(data);

  return {
    left: [
      {
        id: 'main',
        items: [
          listItems.data.spec.name,
          listItems.data.nextObservedDate,
          listItems.data.spec.repeating,
          listItems.data.spec.observance,
          listItems.data.spec.timezone,
        ],
      },
      {
        id: 'date',
        title: 'Date',
        items: [
          listItems.data.spec.type.typeName,
          ...match(listItems.data.spec.type)
            .with({ fixed: P.not(P.nullish) }, ({ fixed }) => [fixed.value.month, fixed.value.day])
            .with({ floating: P.not(P.nullish) }, ({ floating }) =>
              floating.value.summary ? [floating.value.summary] : [floating.value.month, floating.value.dayOfWeek, floating.value.weekOfMonth],
            )
            .with({ calculated: P.not(P.nullish) }, ({ calculated }) => [calculated.value.type])
            .otherwise(() => []),
        ],
      },
      {
        id: 'tradingPatch',
        title: 'Trading',
        items: [
          listItems.data.spec.patch.trading.summary.early,
          listItems.data.spec.patch.trading.summary.core,
          listItems.data.spec.patch.trading.summary.late,
        ],
      },
      {
        id: 'transfersPatch',
        title: 'Transfers',
        items: [
          listItems.data.spec.patch.transfers.summary.wire,
          listItems.data.spec.patch.transfers.summary.achStandard,
          listItems.data.spec.patch.transfers.summary.achSameDay,
        ],
      },
      {
        id: 'moneyMarketsPatch',
        title: 'Money Markets',
        items: [listItems.data.spec.patch.moneyMarket.summary.orders],
      },
    ] satisfies ReadonlyListGroup[],
    right: [listItems.metadata.createdAt, listItems.metadata.updatedAt, listItems.exceptionId] satisfies ReadonlyListItem[],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load exception', 'view this exception');

function getMenuItems(exceptionId: string, status: IxfHoursV1ExceptionStatus | undefined, isClearingUser: boolean): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: HoursExceptionEventDrawer,
        props: { request: { exceptionId } },
      },
    },
    actions: isClearingUser
      ? ([
          ...(!status || status === IxfHoursV1ExceptionStatus.Archived
            ? []
            : [
                {
                  id: 'edit',
                  icon: <EditIcon aria-hidden />,
                  label: 'Edit',
                  interaction: {
                    type: 'drawer',
                    drawer: {
                      Component: HoursExceptionUpdateDrawer,
                      props: { exceptionId },
                    },
                  },
                  requiresWrite: true,
                } satisfies EntityActionMenuItem,
                {
                  id: 'archive',
                  icon: <ArchiveIcon aria-hidden width={16} />,
                  label: 'Archive',
                  tone: 'danger-dk',
                  interaction: {
                    type: 'modal',
                    modal: {
                      Component: ArchiveHoursExceptionDialog,
                      props: { exceptionId },
                    },
                  },
                  requiresWrite: true,
                } satisfies EntityActionMenuItem,
              ]),
        ] satisfies EntityActionMenuItem[])
      : undefined,
  };
}

function HoursExceptionDetail() {
  const { exceptionId } = Route.useParams();
  const { data, error, isPending } = useIxfHoursV1ExceptionQueryServiceExceptionGet({ exceptionId });
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.exception), [data?.exception]);
  const { activeTenantIsClearing } = useActiveRealmAPIContext();
  const menuItems = useMemo(
    () => getMenuItems(exceptionId, data?.exception?.status, activeTenantIsClearing),
    [exceptionId, data?.exception?.status, activeTenantIsClearing],
  );

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <HoursExceptionStatusPill isPending={showPending} size="large" status={data?.exception?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} isPending={showPending} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
